/**
 * BonsaiFirmAssist
 *
 * Inline AI assistant panel for the fee-earner / hub side.
 * Surfaces matter-aware suggestions, risk prompts and next-step
 * recommendations based on the current submission context.
 *
 * Usage:
 *   <BonsaiFirmAssist submissionId={id} matterType={type} />
 */
import { useState, useCallback } from "react";
import { API } from "../config/api";
import axios from "axios";
import {
  Sparkles, Loader2, RefreshCw, ChevronDown, ChevronUp,
  AlertTriangle, CheckCircle2, ArrowRight, Info
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

import { errorMessage } from "../utils/errorMessage";
// ---------------------------------------------------------------------------
// Priority badge
// ---------------------------------------------------------------------------
function PriorityBadge({ level }) {
  const map = {
    high:   "bg-status-red-dim text-status-red border-red-200",
    medium: "bg-status-amber-dim text-status-amber border-amber-200",
    low:    "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${map[level] ?? map.low}`}>
      {level}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Section block used for each result category
// ---------------------------------------------------------------------------
function AssistSection({ icon: Icon, iconClass, title, items, renderItem }) {
  if (!items?.length) return null;
  return (
    <div>
      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
        <Icon className={`w-3.5 h-3.5 ${iconClass}`} />
        {title}
      </h4>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i}>{renderItem(item, i)}</li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function BonsaiFirmAssist({ submissionId, matterType = "", className = "" }) {
  const [loading, setLoading]   = useState(false);
  const [result, setResult]     = useState(null);
  const [expanded, setExpanded] = useState(true);

  const fetchAssist = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/firm-assist/${submissionId}`, {
        matter_type: matterType,
      });
      setResult(res.data);
    } catch (err) {
      // Failure surfaces as a toast (sonner — the app-wide toaster mounted
      // in App.js); the panel falls back to its idle state so the fee earner
      // can simply hit "Run Assist" again. No inline banner.
      const msg = errorMessage(err, "Firm Assist could not load suggestions.");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [submissionId, matterType]);

  // ── idle / not yet fetched (also the post-failure state) ─────────────────
  if (!result) {
    return (
      <div
        className={`border border-teal/20 rounded-md p-4 bg-teal/5 ${className}`}
        data-testid="bonsai-firm-assist-idle"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-teal" />
              <span className="font-medium text-teal">Bonsai Firm Assist</span>
            </div>
            <p className="text-xs text-slate-500">
              AI analysis of this matter — risks, next steps, and points to raise with the client.
            </p>
          </div>
          <Button
            size="sm"
            onClick={fetchAssist}
            disabled={loading}
            className="shrink-0 bg-teal hover:bg-teal-hover text-white gap-1.5"
            data-testid="firm-assist-trigger-btn"
          >
            {loading
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analysing...</>
              : <><Sparkles className="w-3.5 h-3.5" /> Run Assist</>}
          </Button>
        </div>
      </div>
    );
  }

  // ── result panel ─────────────────────────────────────────────────────────
  return (
    <div
      className={`border border-teal/25 rounded-md overflow-hidden bg-white ${className}`}
      data-testid="bonsai-firm-assist-result"
    >
      {/* Header */}
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 bg-teal/6 hover:bg-teal/10 transition-colors"
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-teal" />
          <span className="font-medium text-teal text-sm">Bonsai Firm Assist</span>
          {result.model && (
            <span className="text-xs bg-teal/10 text-teal px-2 py-0.5 rounded-full">
              {result.model}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={e => { e.stopPropagation(); fetchAssist(); }}
            disabled={loading}
            className="p-1 rounded hover:bg-teal/10 text-slate-400 hover:text-teal transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
          {expanded
            ? <ChevronUp className="w-4 h-4 text-slate-400" />
            : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="p-4 space-y-5">

          {/* Matter summary bullets */}
          <AssistSection
            icon={Info} iconClass="text-blue-500"
            title="Matter summary"
            items={result.summary}
            renderItem={(item) => (
              <p className="text-sm text-slate-600 flex items-start gap-2">
                <span className="mt-1 text-blue-400 shrink-0">•</span>{item}
              </p>
            )}
          />

          {/* Risks */}
          <AssistSection
            icon={AlertTriangle} iconClass="text-status-amber"
            title="Risks / issues to flag"
            items={result.risks}
            renderItem={(item) => (
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-status-amber shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-700">{typeof item === "string" ? item : item.text}</p>
                  {item.priority && <PriorityBadge level={item.priority} />}
                </div>
              </div>
            )}
          />

          {/* Next steps */}
          <AssistSection
            icon={ArrowRight} iconClass="text-teal"
            title="Suggested next steps"
            items={result.next_steps}
            renderItem={(item, i) => (
              <div className="flex items-start gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-teal/10 text-teal text-xs flex items-center justify-center font-medium">{i + 1}</span>
                <p className="text-sm text-slate-700">{typeof item === "string" ? item : item.text}</p>
              </div>
            )}
          />

          {/* Points to raise with client */}
          <AssistSection
            icon={CheckCircle2} iconClass="text-slate-400"
            title="Points to raise with client"
            items={result.client_points}
            renderItem={(item) => (
              <p className="text-sm text-slate-600 flex items-start gap-2">
                <span className="mt-1 text-slate-300 shrink-0">—</span>{typeof item === "string" ? item : item.text}
              </p>
            )}
          />

          {/* Disclaimer */}
          <p className="text-xs text-slate-400 pt-2 border-t border-slate-100 flex items-center gap-1">
            <Info className="w-3 h-3 shrink-0" />
            AI analysis only — verify all points before advising. Not a substitute for legal judgment.
          </p>
        </div>
      )}
    </div>
  );
}

export default BonsaiFirmAssist;
